import { bound } from "@frui.ts/helpers";
import { Router } from "@frui.ts/screens/dist";
import { addError, attachManualValidator, getValidationMessage, removeError } from "@frui.ts/validation";
import { onlyResolvesLast } from "awesome-only-resolves-last-promise/dist";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import { plainToClass } from "class-transformer";
import { action, computed, observable, reaction } from "mobx";
import NiaResponse from "models/niaResponse";
import ChargeRegistrationService from "services/chargeRegistrationService";
import EnumService from "services/enumService";
import EventBus, {
  licensePlateChangeCreationFailed,
  licensePlateChangeDocumentsUploadFailed,
  licensePlateChangeDocumentsUploadSuccess,
  licensePlateChangeISDSConceptStatusCheckFailure,
  licensePlateChangeISDSConceptStatusCheckSuccess,
  licensePlateChangeISDSSessionIdHandled,
  licensePlateChangeNIAResponseHandled,
} from "services/eventBus";
import LicensePlateChangeService from "services/licensePlateChangeService";
import LocalizationService from "services/localizationService";
import NiaService, { Notice } from "services/NIA/niaService";
import { SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY } from "services/omissionService";
import AppRouter from "services/appRouter";
import VehicleInfoViewModel from "viewModels/common/vehicleInfoViewModel";
import { getTooltipPosition } from "viewModels/utils";
import ISDSService from "../../../services/ISDS/isdsService";
import { repeatIfFailed } from "../../../services/requestRecoveryService";
import serializeNotice from "../../../utils/serializeNotice";
import NoticeViewModel from "../noticeViewModel";
import debounce from "debounce-promise";
import ApplicantPersonViewModel from "../components/applicantPersonViewModel";
import OutageService from "../../../services/outageService";
import OutagesViewModel from "../../common/outagesViewModel";
import VehicleChargingInstructionsResponse from "entities/vehicleChargingInstructionsResponse";
import ExemptionReasonResponse from "entities/exemptionReasonResponse";
import VehicleChangeApplicationProductionResponse from "entities/vehicleChangeApplicationProductionResponse";
import { czechID } from "config";
import { PersistedBase64, SignatureType } from "models/notice";
import { NoticeTechnicalLicenseRules } from "models/licensePlateChange";

export const LicensePlateChangeNoticeNavigationName = "license_plate_change_notice";

@Router.registerRoute({ route: LicensePlateChangeNoticeNavigationName, name: Router.Self })
export default class LicensePlateChangeViewModel extends NoticeViewModel {
  @observable tooltipPosition: "right-start" | "bottom";
  @observable applicantPersonViewModel: ApplicantPersonViewModel;
  @observable originalVehicleInfoViewModel: VehicleInfoViewModel;
  @observable newVehicleInfoViewModel: VehicleInfoViewModel;
  @observable allowCreateExemption = false;
  @observable loadingInstruction = false;
  private debouncedLookForChargeRegistration = debounce(this.lookForChargeRegistration, 400);
  private lastResolveFindChargeRegistration = onlyResolvesLast(this.findChargeRegistration);

  @computed
  get newVehicleErrorMessage() {
    return getValidationMessage(this, "notice");
  }

  @computed
  get notice() {
    return this.service.notice;
  }

  @computed
  get hasToUploadPowerOfAttorney() {
    return false;
  }

  @computed
  get hasToUploadTechnicalLicense() {
    return this.notice._signatureType === SignatureType.DataBox;
  }

  constructor(
    public localizationService: LocalizationService,
    protected enumService: EnumService,
    protected niaService: NiaService,
    protected isdsService: ISDSService,
    protected outageService: OutageService,
    protected eventBus: EventBus,
    protected service: LicensePlateChangeService,
    protected router: AppRouter,
    protected outagesViewModelFactory: ReturnType<typeof OutagesViewModel.Factory>,
    private chargeRegistrationService: ChargeRegistrationService,
    private vehicleInfoVMFactory: ReturnType<typeof VehicleInfoViewModel.Factory>,
    private applicantPersonInputVMFactory: ReturnType<typeof ApplicantPersonViewModel.Factory>
  ) {
    super(
      localizationService,
      enumService,
      niaService,
      isdsService,
      outageService,
      eventBus,
      service,
      router,
      outagesViewModelFactory
    );
    this.navigationName = LicensePlateChangeNoticeNavigationName;
    this.createChildren();
    window.addEventListener("resize", this.handleResize);
    this.setDefaultCountryId();
    if (!this.notice.countryId && this.localizationService.languageCode === "CS") {
      this.notice.countryId = czechID;
    }
    attachManualValidator(this, true);
    this.attachValidators();

    this.unsubscriptions.push(
      this.eventBus.subscribe(licensePlateChangeCreationFailed, this.generalOperationFailed),
      this.eventBus.subscribe(licensePlateChangeDocumentsUploadFailed, this.isdsOperationFailed),
      this.eventBus.subscribe(licensePlateChangeDocumentsUploadSuccess, this.isdsUploadOperationSuccess),
      this.eventBus.subscribe(licensePlateChangeISDSConceptStatusCheckSuccess, this.isdsConceptStatusCheck),
      this.eventBus.subscribe(licensePlateChangeISDSConceptStatusCheckFailure, this.isdsConceptFailure),
      this.eventBus.subscribe(licensePlateChangeNIAResponseHandled, this.NiaResponseHandler),
      this.eventBus.subscribe(licensePlateChangeISDSSessionIdHandled, this.ISDSSessionIdHandler)
    );
  }

  @computed
  get vehicleChargingInstructions(): VehicleChargingInstructionsResponse | undefined {
    return this.chargeRegistrationService.vehicleChargingInstructions;
  }

  @computed
  get vehicleChangeApplicationProduction(): VehicleChangeApplicationProductionResponse | undefined {
    return this.service.vehicleChangeApplicationProduction;
  }

  @computed
  get loadingData() {
    return this.loadingInstruction;
  }

  @computed
  get isContinueDisabled(): boolean {
    return this.service.vehicleChangeApplicationProduction?.canBeAppliedFor === false;
  }

  @bound
  translateGeneral(code: string, disableUnbreakableRules = false) {
    return this.localizationService.translateGeneral(`licensePlateChange.${code}`, undefined, disableUnbreakableRules);
  }

  @bound
  translateAttribute(code: string) {
    return this.localizationService.translateAttribute("licensePlateChange", code);
  }

  @bound
  showBirthdate() {
    return false;
  }

  @bound
  translateWarningReason(code: string, possibleExemptionReasonIds?: string[]): string {
    if (code === "IDENTIFIED_POSSIBLE_EXEMPTION") {
      if (!possibleExemptionReasonIds || possibleExemptionReasonIds.length < 1) {
        return this.localizationService.translateGeneral("errors.generic");
      }
      const possibleExemptionReasonId = possibleExemptionReasonIds[0];
      const exemptionReasons = this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
      const matchingReason = exemptionReasons.find((x) => x.id === possibleExemptionReasonId);
      switch (matchingReason?.exemptionReason) {
        case "C":
          return this.translateWarningReason("IDENTIFIED_POSSIBLE_EXEMPTION_C", possibleExemptionReasonIds);
        case "P":
          return this.translateWarningReason("IDENTIFIED_POSSIBLE_EXEMPTION_P", possibleExemptionReasonIds);
        case "O":
        case "O1":
        case "O2":
          return this.translateWarningReason("IDENTIFIED_POSSIBLE_EXEMPTION_O", possibleExemptionReasonIds);
        default:
          return this.localizationService.translateGeneral("errors.generic");
      }
    }
    if (this.service.vehicleChangeApplicationProduction?.chargeManagementUrl) {
      return this.localizationService
        .translateGeneral(`eshop.order.license.exemptionReasonWarnings.${code}`, undefined, false)
        .replace("%link%", this.service.vehicleChangeApplicationProduction?.chargeManagementUrl);
    }
    return this.localizationService.translateGeneral(`eshop.order.license.exemptionReasonWarnings.${code}`, undefined, false);
  }

  protected async onInitialize(): Promise<any> {
    this.enumService.values("exemption_reasons") as ExemptionReasonResponse[];
    this.unsubscriptions.push(
      reaction(() => [this.notice.licensePlate, this.notice.countryId], this.chargeRegistrationReaction, {
        fireImmediately: true,
      }),
      reaction(
        () => [this.notice.vehicle?.licensePlate, this.notice.vehicle?.countryId],
        () => {
          this.allowCreateExemption = false;
          this.service.vehicleChangeApplicationProduction = undefined;
          this.chargeRegistrationService.vehicleChargingInstructions = undefined;
        }
      ),
      reaction(() => [this.notice.licensePlate, this.notice.countryId], this.persistNotice),
      reaction(
        () => [
          this.notice.licensePlate,
          this.notice.countryId,
          this.notice.vehicle?.licensePlate,
          this.notice.vehicle?.countryId,
        ],
        this.sameVehicleCheck,
        { fireImmediately: true }
      )
    );
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean) {
    if (isClosing) {
      this.unsubscriptions.forEach((unsubscribe) => unsubscribe());
    }
    window.removeEventListener("resize", this.handleResize);
    return super.onDeactivate(isClosing);
  }

  handleResize = () => {
    this.tooltipPosition = getTooltipPosition(window);
  };

  private translateOrderItem(code: string) {
    return this.localizationService.translateAttribute("orderItem", code);
  }

  async navigate(subPath: string | undefined, params: any) {
    if (!params) return;

    if (params.sessionId) {
      if (sessionStorage.getItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY)) {
        sessionStorage.removeItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY);
        await this.isdsService.conceptStatus(params.sessionId, Notice.LICENSE_PLATE_CHANGE);
        return;
      }
      this.eventBus.publish(licensePlateChangeISDSSessionIdHandled({ sessionId: params.sessionId }));
    } else if (params.response) {
      const response = params.response;

      if (response && this.notice && this.notice.applicant) {
        const urlDecoded = decodeURI(response);
        const base64Decoded = atob(urlDecoded);
        const niaResponse = plainToClass(NiaResponse, JSON.parse(base64Decoded));

        if (!niaResponse) return;

        this.eventBus.publish(licensePlateChangeNIAResponseHandled({ niaResponse }));
      }
    }
  }

  @action.bound
  protected isdsConceptStatusCheck(data: { type: string; payload: { success: boolean; notice: Notice } }) {
    if (data.payload.success && data.payload.notice) {
      this.router.navigate("license_plate_change_documents", undefined, {
        notice: data.payload.notice,
      });
    }
  }

  @action.bound
  protected isdsUploadOperationSuccess(data: { type: string; payload: { conceptIdent: string } }) {
    super.isdsUploadOperationSuccess(data);
    this.isdsService.redirectToISDSConcept(Notice.LICENSE_PLATE_CHANGE, data.payload.conceptIdent);
  }

  @bound
  sameVehicleCheck() {
    removeError(this, "notice");
    if (
      this.notice.countryId &&
      this.notice.licensePlate &&
      this.notice.vehicle?.countryId === this.notice.countryId &&
      this.notice.vehicle?.licensePlate === this.notice.licensePlate
    ) {
      addError(this, "notice", this.localizationService.translate("validators.sameVehicle"));
    }
  }

  @bound
  private async chargeRegistrationReaction() {
    this.allowCreateExemption = false;
    this.service.vehicleChangeApplicationProduction = undefined;
    this.chargeRegistrationService.vehicleChargingInstructions = undefined;
    if (this.notice.countryId && this.notice.licensePlate) {
      try {
        this.loadingInstruction = true;
        await this.debouncedLookForChargeRegistration();
        this.loadingInstruction = false;
      } catch (e) {
        this.loadingInstruction = false;
        // Silent catch - background task with auto recovery mechanism
      }
    }
  }

  @action.bound
  @repeatIfFailed
  private async lookForChargeRegistration() {
    const countryId = this.notice.countryId;
    const licensePlate = this.notice.licensePlate;

    if (!countryId || !licensePlate) return;

    this.chargeRegistrationService.reset();

    try {
      await this.lastResolveFindChargeRegistration(countryId, licensePlate);
    } catch (e) {
      throw new Error("Registration check failed");
    }
  }

  @bound
  private findChargeRegistration(countryId: string, licensePlate: string) {
    return this.chargeRegistrationService.getChargingInstructions(countryId, licensePlate);
  }

  protected attachValidators() {
    attachAutomaticBlurValidator(this.notice, {
      ...this.notice.rules,
      ...NoticeTechnicalLicenseRules,
    });
  }

  @bound
  protected async persistNotice() {
    sessionStorage.setItem(Notice.LICENSE_PLATE_CHANGE, serializeNotice(this.notice));

    await super.persistNotice();
  }

  @action.bound
  public async createNotice(): Promise<string | undefined> {
    this.errorMessage = undefined;
    const countryId = this.notice.vehicle?.countryId;
    const licensePlate = this.notice.vehicle?.licensePlate;

    if (!countryId || !licensePlate) return Promise.resolve(undefined);

    const response = (await this.service.getProduction(this.notice)) as any;

    if (response.errors) {
      this.creationLoadingModalToggle(false);
      this.errorMessage = this.localizationService.translateGeneral("errors.invalidData");
      return;
    }

    if (
      (this.vehicleChangeApplicationProduction?.warningReasons?.length ||
        this.vehicleChangeApplicationProduction?.rejectionReason ||
        this.vehicleChangeApplicationProduction?.canBeAppliedFor === false) &&
      !this.allowCreateExemption
    ) {
      this.allowCreateExemption = true;

      return Promise.resolve(undefined);
    }

    return this.service.createLicensePlateChangeNotice(this.notice);
  }

  @action.bound
  public uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]): Promise<void> | undefined {
    this.errorMessage = undefined;
    const countryId = this.notice.vehicle?.countryId;
    const licensePlate = this.notice.vehicle?.licensePlate;

    if (!countryId || !licensePlate) return;

    return this.service.uploadDocuments(ISDSSessionId, files);
  }

  @action.bound
  public redirectToISDS(): void {
    this.errorMessage = undefined;
    this.creationLoadingModalToggle(false);
    this.isdsService.redirectToISDS(Notice.LICENSE_PLATE_CHANGE);
  }

  @action.bound
  public redirectToNia() {
    this.errorMessage = undefined;
    this.creationLoadingModalToggle(false);
    this.niaService.redirectToNia(Notice.LICENSE_PLATE_CHANGE);
  }

  protected createChildren() {
    this.children.clear();
    this.children.push(
      (this.originalVehicleInfoViewModel = this.vehicleInfoVMFactory(this.notice.vehicle!, {
        attachValidator: true,
        fixedLicensePlateSize: false,
        initByLocalization: true,
      })),
      (this.newVehicleInfoViewModel = this.vehicleInfoVMFactory(this.notice, { attachValidator: false })),
      (this.applicantPersonViewModel = this.applicantPersonInputVMFactory(this.notice.applicant, this.GDPR, this.showBirthdate()))
    );
  }
}
